/*
 * VRéserve Video tabs (sections/vr-video-tabs.liquid).
 * Accessible tabs (WAI-ARIA tabs pattern, automatic activation): click,
 * swipe (touch) or Left/Right/Home/End to switch. Each tab swaps the background video, text
 * and button. Only the visible video plays; videos stay paused for visitors
 * who prefer reduced motion.
 */
if (!customElements.get('vr-video-tabs')) {
  customElements.define(
    'vr-video-tabs',
    class VrVideoTabs extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const { signal } = this.abort;

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.media = Array.from(this.querySelectorAll('.vr-video-tabs__media-item'));
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index), { signal });
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
        });

        // Horizontal swipe anywhere on the section moves to the next/previous tab
        this.addEventListener(
          'touchstart',
          (event) => {
            const touch = event.touches[0];
            this.touchStart = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
          },
          { passive: true, signal }
        );
        this.addEventListener(
          'touchend',
          (event) => {
            if (!this.touchStart) return;
            const touch = event.changedTouches[0];
            const dx = touch.clientX - this.touchStart.x;
            const dy = touch.clientY - this.touchStart.y;
            this.touchStart = null;
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
            const last = this.tabs.length - 1;
            const rtl = getComputedStyle(this).direction === 'rtl';
            const forward = rtl ? dx > 0 : dx < 0;
            this.select(forward ? (current === last ? 0 : current + 1) : current === 0 ? last : current - 1);
          },
          { passive: true, signal }
        );

        // Play only while the section is on screen and the page is visible
        this.inView = false;
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some((entry) => entry.isIntersecting);
            this.syncPlayback();
          },
          { threshold: 0.15 }
        );
        this.observer.observe(this);
        document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });

        const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this.select(current < 0 ? 0 : current, false);
      }

      syncPlayback() {
        const shouldPlay = this.inView && !document.hidden && !this.reduceMotion.matches;
        this.media.forEach((item) => {
          const video = item.querySelector('video');
          if (!video) return;
          if (shouldPlay && item.classList.contains('is-active')) {
            if (video.preload === 'none') {
              video.preload = 'auto';
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      disconnectedCallback() {
        this.abort?.abort();
        this.observer?.disconnect();
      }

      /** @param {KeyboardEvent} event @param {number} index */
      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next = null;
        if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = index === last ? 0 : index + 1;
        if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = index === 0 ? last : index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.select(next, true);
      }

      /** @param {number} index @param {boolean} [focus] */
      select(index, focus = false) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', `${active}`);
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });
        this.media.forEach((item, i) => item.classList.toggle('is-active', i === index));
        this.syncPlayback();
      }
    }
  );
}
